/**
 * Breadcrumb navigation container for the player page header
 *
 * Reads the breadcrumb stack from NavigationProvider and pre-builds
 * press handlers for each segment to avoid inline functions in the View.
 * Navigates back to the pressed segment's path using router.navigate.
 * @module features/player-detail/components/Breadcrumb/BreadcrumbContainer
 */

import { type Href, useRouter } from "expo-router";
import React, { memo, useMemo } from "react";

import BreadcrumbView from "./BreadcrumbView";

import {
  globalSearchOpenVar,
  globalSearchReopenForPathVar,
} from "@/stores/globalSearchNavigation";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useNavigationContext } from "@/providers/NavigationProvider";

/**
 * Container for breadcrumb navigation — reads context and builds segments
 */
const BreadcrumbContainer = () => {
  const router = useRouter();
  const isSmallScreen = useMobileScreen();
  const { breadcrumbs, popToIndex, clearBreadcrumbs } = useNavigationContext();

  /** Pre-build press handlers for each breadcrumb entry */
  const pressHandlers = useMemo(
    () =>
      breadcrumbs.reduce(
        (acc, entry, index) => ({
          ...acc,
          [entry.path]: () => {
            // Index 0 fully resets the trail; deeper crumbs pop back to their
            // level so the entries above the pressed one are preserved.
            if (index === 0) {
              clearBreadcrumbs();
            } else {
              popToIndex(index);
            }
            // "/search" is the mobile-only search TAB; on desktop web it
            // renders a blank page (the web search surface is a modal, opened
            // from the sidebar). Rather than dead-end the breadcrumb back on a
            // non-existent route, reopen the global-search modal over the
            // current route — matching the desktop web search UX. Gated on
            // screen size (not platform) since /search is a valid route on the
            // mobile bottom-tab layout, including narrow web.
            if (entry.path === "/search" && !isSmallScreen) {
              globalSearchOpenVar(true);
              return;
            }
            router.navigate(entry.path as Href);
            // SE-5387: a search-origin crumb must reopen the Global Search
            // modal over the destination tab — navigating alone lands on a bare
            // tab with no search context. We must NOT open the modal in this
            // same tick: GlobalSearchModalContainer's SE-5185 teardown closes
            // the overlay on any pathname change while it is open, so a
            // same-tick reopen is immediately stomped by the route transition.
            // Instead we record the destination pathname as a deferred-reopen
            // intent; the modal container reopens itself only AFTER the route
            // settles on that pathname (a later commit), so the reopen survives
            // the navigation.
            if (entry.reopenSearch) {
              globalSearchReopenForPathVar(entry.path);
            }
          },
        }),
        {} as Record<string, () => void>
      ),
    [breadcrumbs, clearBreadcrumbs, popToIndex, router, isSmallScreen]
  );

  /** Map breadcrumb entries to View-compatible segments with handlers */
  const segments = useMemo(
    () =>
      breadcrumbs.map((entry, index) => ({
        label: entry.label,
        key: entry.path,
        isCurrent: index === breadcrumbs.length - 1,
        onPress:
          index < breadcrumbs.length - 1
            ? pressHandlers[entry.path]
            : undefined,
      })),
    [breadcrumbs, pressHandlers]
  );

  // Require at least 2 breadcrumbs (section + player) to render.
  // A single entry means stale state from a previous navigation.
  return breadcrumbs.length >= 2 ? (
    <BreadcrumbView segments={segments} />
  ) : null;
};

BreadcrumbContainer.displayName = "BreadcrumbContainer";

export default memo(BreadcrumbContainer);
